import { cn } from '@/lib/cn'

/**
 * Invisible column matching {@link PageHeader} icon width so content below (e.g. cards)
 * aligns with the title text, not the main content edge.
 */
export function PageHeaderIconSpacer(): React.ReactElement {
  return (
    <span
      className="pointer-events-none shrink-0 select-none rounded-lg border border-transparent p-2.5 opacity-0"
      aria-hidden
    >
      <span className="block h-5 w-5" />
    </span>
  )
}

PageHeaderIconSpacer.displayName = 'PageHeaderIconSpacer'

export interface PageHeaderProps {
  icon?: React.ComponentType<{ className?: string }>
  title: string
  description?: string
  actions?: React.ReactNode
  className?: string
}

/**
 * Title row with optional icon, description, and actions.
 * Prefer {@link PageShell} for full pages so breadcrumb + title + body stay in order.
 */
export function PageHeader({
  icon: Icon,
  title,
  description,
  actions,
  className,
}: PageHeaderProps): React.ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between sm:gap-8',
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-x-6 gap-y-4">
        {Icon && (
          <span className="shrink-0 rounded-lg bg-primary/10 p-2.5 dark:bg-primary/20">
            <Icon className="h-5 w-5 text-primary" aria-hidden />
          </span>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
            {title}
          </h1>
          {description && (
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex shrink-0 flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-3">
          {actions}
        </div>
      )}
    </div>
  )
}

PageHeader.displayName = 'PageHeader'
